export const paths = {
  root: '/',
  login: '/login',

  auth: {
    signIn: '/auth/sign-in',
    jwt: {
      forgotPassword: '/auth/forgot-password',
      passwordExpired: '/auth/password-expired',
      verifyCode: '/auth/verify-code',
      twoFactor: '/auth/two-factor',
    },
  },
  terms: '/terms',
  accountSettings: '/account-settings',
  dashboard: {
    index: '/dashboard',
  },
  administrator: {
    dashboard: '/administrator/dashboard',
    index: '/administrator',
    users: {
      index: '/administrator/users',
      demos: '/administrator/users/demos',
    },
    aggregation: {
      index: '/administrator/aggregation',
      errors: '/administrator/aggregation/errors',
      institutions: '/administrator/aggregation/institutions',
      assetClassification: '/administrator/aggregation/assetclassification',
      firmDataFeeds: '/administrator/aggregation/firmDataFeeds',
    },
    integrations: {
      index: '/administrator/integrations',
      firstrate: '/administrator/integrations/firstrate',
      orion: '/administrator/integrations/orion',
      trustdesk: '/administrator/integrations/trustdesk',
    },
    system: {
      index: '/administrator/system',
    },
  },
  investor: {
    dashboard: '/investor/dashboard',
    accounts: '/accounts',
    cashFlow: '/cash-flow',
    documents: {
      index: '/documents',
      documentVault: '/documents/document-vault',
      reports: '/documents/reports',
      notes: '/documents/notes',
    },
    portfolio: {
      index: '/portfolio',
      accounts: '/portfolio/accounts',
      accountDetail: '/portfolio/accounts/:accountNumber',
      holdings: '/portfolio/holdings',
      assetAllocation: '/portfolio/asset-allocation',
      gainsLosses: '/portfolio/gains-losses',
      investmentActivity: '/portfolio/investment-activity',
      marketPricing: '/portfolio/market-pricing',
      performance: '/portfolio/performance',
    },
    balanceSheet: '/balance-sheet',
    team: '/investor/team',
  },
  advisor: {
    dashboard: '/advisor/dashboard',
    clients: {
      index: '/advisor/clients',
      prospects: '/advisor/clients/prospects',
      activity: '/advisor/clients/activity',
      content: '/advisor/clients/content',
    },
    clientsWealth: {
      index: '/advisor/clients-wealth',
      accounts: '/advisor/clients-wealth/accounts',
      holdings: '/advisor/clients-wealth/holdings',
      assetAllocation: '/advisor/clients-wealth/asset-allocation',
      balanceSheet: '/advisor/clients-wealth/balance-sheet',
    },
    businessIntelligence: {
      index: '/advisor/business-intelligence',
      insights: '/advisor/business-intelligence/insights',
      opportunities: '/advisor/business-intelligence/opportunities',
      risks: '/advisor/business-intelligence/risks',
      toDoList: '/advisor/business-intelligence/to-do-list',
      metrics: '/advisor/business-intelligence/metrics',
      aumHeldAway: '/advisor/business-intelligence/aum-held-away',
    },
    documents: {
      index: '/advisor/documents',
      documentVault: '/advisor/documents/document-vault',
      notes: '/advisor/documents/notes',
      intelligence: '/advisor/documents/intelligence',
    },
    reports: '/advisor/reports',
    resources: '/advisor/resources',
    configuration: {
      index: '/advisor/configuration',
      advisorAndClientPortals:
        '/advisor/configuration/advisor-and-client-portals',
      integrations: '/advisor/configuration/integrations',
      legal: {
        index: '/advisor/configuration/legal',
        disclaimer: '/advisor/configuration/legal/disclaimer',
      },
      groupsAndUsers: '/advisor/configuration/groups-and-users',
      dataFeeds: {
        index: '/advisor/configuration/data-feeds',
        managedAccounts: '/advisor/configuration/data-feeds/managed-accounts',
        assetAllocation: '/advisor/configuration/data-feeds/asset-allocation',
        managedLinkedInstitutions:
          '/advisor/configuration/data-feeds/managed-linked-institutions',
      },
      managedAccounts: {
        index: '/advisor/configuration/managed-accounts',
        dataFeeds: '/advisor/configuration/managed-accounts/data-feeds',
        accountTags: '/advisor/configuration/managed-accounts/account-tags',
        assetClassifications:
          '/advisor/configuration/managed-accounts/asset-classifications',
        managedAssets: '/advisor/configuration/managed-accounts/managed-assets',
        // Assign & Edit Accounts is a static route; the feed travels as a query
        // param. AGENTS.md: URL state uses query params, never path params, so
        // route permission stays exact set membership (no wildcard grant).
        // The page itself arrives with a later slice of this series; until then
        // the section's catch-all redirects this path to the default tab.
        dataFeedAccounts:
          '/advisor/configuration/managed-accounts/data-feeds/accounts',
        dataFeedAccountsFor: (feedId: number | string) =>
          `/advisor/configuration/managed-accounts/data-feeds/accounts?feedId=${String(feedId)}`,
      },
      teamMember: '/advisor/configuration/team-member',
    },
    admin: {
      index: '/admin',
      userAdministration: '/admin/user-administration',
    },
  },
  firm: {
    dashboard: '/firm/dashboard',
    advisorServices: {
      index: '/firm/advisor-services',
      advisors: '/firm/advisor-services/advisors',
      activity: '/firm/advisor-services/activity',
    },
    clientUsage: {
      index: '/firm/client-usage',
      activity: '/firm/client-usage/activity',
      eConsents: '/firm/client-usage/econsents',
    },
    analytics: {
      assetAllocation: '/firm/business-intelligence/asset-allocation',
    },
    businessIntelligence: {
      index: '/firm/business-intelligence',
      metrics: '/firm/business-intelligence/metrics',
      aumHeldAway: '/firm/business-intelligence/aum-held-away',
      assetAllocation: '/firm/business-intelligence/asset-allocation',
      insights: '/firm/business-intelligence/insights',
    },
    configuration: {
      index: '/firm/configuration',
      dashboard: {
        index: '/firm/configuration/dashboard',
        quickLinks: '/firm/configuration/dashboard/quick-links',
        promptWindow: '/firm/configuration/dashboard/prompt-window',
      },
      legal: {
        index: '/firm/configuration/legal',
        disclaimer: '/firm/configuration/legal/disclaimer',
      },
      groupsAndUsers: '/firm/configuration/groups-and-users',
      clientDashboard: '/firm/configuration/client-dashboard',
      clientRelationships: '/firm/configuration/client-relationships',
      portalSettings: '/firm/configuration/advisor-and-client-portals',
      advisorAndClientPortals: {
        index: '/firm/configuration/advisor-and-client-portals',
        privateLabel:
          '/firm/configuration/advisor-and-client-portals/private-label',
        advisorDashboard:
          '/firm/configuration/advisor-and-client-portals/advisor-dashboard',
        clientInvitation:
          '/firm/configuration/advisor-and-client-portals/client-invitation',
        permissions:
          '/firm/configuration/advisor-and-client-portals/permissions',
      },
      dataFeeds: {
        index: '/firm/configuration/data-feeds',
        managedAccounts: '/firm/configuration/data-feeds/managed-accounts',
        assetAllocation: '/firm/configuration/data-feeds/asset-allocation',
        managedLinkedInstitutions:
          '/firm/configuration/data-feeds/managed-linked-institutions',
      },
      integrations: '/firm/configuration/integrations',
    },
    reports: '/firm/reports',
    admin: {
      index: '/firm/admin',
      userAdministration: '/firm/admin/user-administration',
      aggregationStatus: '/firm/admin/aggregation-status',
    },
  },
  banker: {
    businessIntelligence: {
      index: '/business-intelligence',
      metrics: '/business-intelligence/metrics',
      aumHeldAway: '/business-intelligence/aum-held-away',
      insights: '/business-intelligence/insights',
    },
    clientsWealth: {
      index: '/clients-wealth',
      accounts: '/clients-wealth/accounts',
      holdings: '/clients-wealth/holdings',
      assetAllocation: '/clients-wealth/asset-allocation',
      balanceSheet: '/clients-wealth/balance-sheet',
    },
  },
  owner: {
    balanceSheet: '/balance-sheet',
    accounts: '/accounts',
    transactions: '/transactions',
    documents: {
      index: '/documents',
      documentVault: '/documents/document-vault',
      reports: '/documents/reports',
    },
    team: '/team',
  },
  teamMember: {
    index: '/teammember',
  },
};
